import type { LogContext, LogFields } from './types'
import { captureException, captureMessage, getClient, withScope } from '@sentry/core'
import { redactFields } from './redact-fields'
import { RESERVED_KEYS } from './serialize'

export interface ReportErrorInput {
  logger: string
  message: string
  fields: LogFields
  context: LogContext | undefined
}

const TAG_KEYS = ['requestId', 'runId', 'userId', 'projectId'] as const

export function reportErrorToSentry({ logger, message, fields, context }: ReportErrorInput): void {
  try {
    if (!getClient()) {
      return
    }
    const { err } = fields
    const rest = Object.fromEntries(
      Object.entries(fields).filter(([key]) => key !== 'err' && key !== '__noReport' && !RESERVED_KEYS.has(key)),
    )
    const extra = err === undefined || err instanceof Error ? rest : { ...rest, err: String(err) }
    withScope((scope) => {
      scope.setTag('logger', logger)
      for (const key of TAG_KEYS) {
        const value = context?.[key]
        if (value !== undefined) {
          scope.setTag(key, String(value))
        }
      }
      scope.setContext('log', { ...redactFields(extra), message })
      scope.setLevel('error')
      if (err instanceof Error) {
        captureException(err)
      } else {
        scope.setFingerprint([logger, message])
        captureMessage(message, 'error')
      }
    })
  } catch {
    // reporting is best effort and must never break the caller
  }
}
